import { Fragment, useMemo, useState } from "react";
import { AiOutlineExclamationCircle } from "react-icons/ai";
import { NavLink, useNavigate } from "react-router-dom";
import {
  TriageIssueType,
  useGetFieldValuesQuery,
  useSearchUsingQueryQuery,
} from "../__generated__/graphql";
import ApplicationPage from "../components/ApplicationPage";
import Table from "../components/Table";
import { useCompany } from "../contexts/CompanyContext";
import {
  FiltersFromMap,
  getFilterKeyMap,
  useTableParams,
} from "../hooks/useTableParams";
import formatRelativeTime from "../utils/dateFns";
import { isFilterActive } from "../utils/filters";
import {
  DateFilterValue,
  MultiSelectValue,
  TableColumn,
} from "../utils/interfaces";
import SearchQuery from "../utils/SearchQuery";

const COLUMNS: Array<TableColumn<string>> = [
  { id: "id", label: "Triage ID" },
  {
    id: "lastSeenTriage",
    label: "Last detected",
    format: (value: string) => formatRelativeTime(value),
  },
  {
    id: "createdAt",
    label: "Date created",
    order: true,
    filter: {
      type: "dateRange",
      options: {
        title: "Created in",
        disableFuture: true,
      },
    },
    format: (value: string) => formatRelativeTime(value),
  },
  {
    id: "createdBy",
    label: "Created by",
    align: "center",
    filter: {
      type: "select",
      options: {
        label: "Created by",
        multiple: true,
      },
    },
    format: (value: string, row: Record<string, any>) => {
      if (value === "System") {
        return row.createdBySystem === "System" ? "Manually" : "Automatically";
      }
      return (
        <div className='flex justify-center'>
          <img
            title={row.createdByUser.email}
            src={row.createdByUser.avatarUrl}
            alt={row.createdByUser.email}
            className='w-8 h-8 rounded-full'
            data-testid='userAvatar'
          />
        </div>
      );
    },
  },
  {
    id: "issues",
    label: "Issues",
    filter: {
      type: "boolean",
      options: {
        label: "Has Associated Issue?",
      },
    },
    format: (value: TriageIssueType[]) => (
      <div className='flex gap-x-1'>
        {value.map(({ id, title }, index) => (
          <Fragment key={id}>
            <NavLink
              to={`/issues/${id}`}
              className='font-medium text-blue-600 dark:text-blue-500 hover:underline'
              onClick={(e) => e.stopPropagation()}
            >
              {title}
            </NavLink>
            {index < value.length - 1 && (
              <span className='text-gray-500'>|</span>
            )}
          </Fragment>
        ))}
      </div>
    ),
  },
  {
    id: "severity",
    label: "Severity",
    align: "center",
    format: () => (
      <div className='flex justify-center'>
        <AiOutlineExclamationCircle size={18} className='text-zinc-400' />
      </div>
    ),
  },
];

const filterKeyMap = getFilterKeyMap(COLUMNS);
type FilterKeyMap = typeof filterKeyMap;

const getSearchQuery = ({
  createdAt,
  createdBy,
  issues,
}: FiltersFromMap<FilterKeyMap>) => {
  const searchQuery = new SearchQuery();

  if (isFilterActive("dateRange", createdAt)) {
    searchQuery.addDateRangeParameter(
      "created_at",
      createdAt as DateFilterValue,
    );
  }

  if (isFilterActive("select", createdBy)) {
    searchQuery.addSearchParameters(
      "created_by_user__email",
      "==",
      createdBy as MultiSelectValue,
    );
  }

  if (issues) {
    searchQuery.addSearchParameter("issues__id", "!=", "Null");
  }

  return searchQuery.toString();
};

const TriagesPage = () => {
  const { selectedCompany } = useCompany();
  const [results, setResults] = useState<any[]>([]);
  const [totalItemsCount, setTotalItemsCount] = useState(0);
  const navigate = useNavigate();

  const {
    filters,
    setFilters,
    pagination,
    setPagination,
    ordering,
    setOrdering,
  } = useTableParams({
    filterKeyMap,
    defaultOrdering: { orderBy: "createdAt" },
    storageKey: "triages",
  });

  const { data } = useGetFieldValuesQuery({
    variables: {
      searchEntity: "Triages",
      fieldName: "created_by_user__email",
      ...(selectedCompany && {
        companyId: parseInt(selectedCompany.value, 10),
      }),
    },
  });

  const creators = data?.getFieldValues || [];

  const { loading } = useSearchUsingQueryQuery({
    variables: {
      searchEntity: "triages",
      searchQuery: getSearchQuery(filters),
      limit: pagination.rowsPerPage,
      offset: pagination.page * pagination.rowsPerPage,
      companyId: selectedCompany ? parseInt(selectedCompany.value, 10) : 0,
      orderBy: `${ordering.order === "asc" ? "-" : ""}created_at`,
    },
    onCompleted: (data) => {
      const results = data?.searchUsingQuery?.results.items || [];
      const totalItemsCount =
        data?.searchUsingQuery?.results.totalItemsCount || 0;
      setResults(results);
      setTotalItemsCount(totalItemsCount);
    },
  });

  const handleRowClick = (id: string) => {
    navigate(`/triage/${id}`, {
      state: { from: "Triages Page" },
    });
  };

  const dynamicFiltersOptions = useMemo(
    () => ({
      createdBy: creators.map((creator) => ({
        id: creator,
        name: creator,
      })),
    }),
    [creators],
  );

  return (
    <ApplicationPage pageHeaderContent='Triages'>
      <div className='h-[calc(100%-44px)]'>
        <div className='h-full'>
          <Table
            columns={COLUMNS}
            count={totalItemsCount}
            idKey='id'
            loading={loading}
            order={ordering.order}
            orderBy={ordering.orderBy}
            page={pagination.page}
            rows={results}
            rowsPerPage={pagination.rowsPerPage}
            handleChangeOrder={setOrdering}
            handleChangeRowsPerPage={(rowsPerPage: number) =>
              setPagination((prev) => ({ ...prev, rowsPerPage }))
            }
            onClick={handleRowClick}
            setPage={(page: number) =>
              setPagination((prev) => ({ ...prev, page }))
            }
            filters={filters}
            setFilters={setFilters}
            dynamicFiltersOptions={dynamicFiltersOptions}
          />
        </div>
      </div>
    </ApplicationPage>
  );
};

export default TriagesPage;
